import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { keys, useCompany } from "../../hooks/queries";
import { api } from "../../lib/api";
import type { Company } from "../../lib/types";
import { SectionHeader } from "../ui/Badges";

type Form = Company;
const EMPTY: Form = { stationName: "", stationNameAr: "", phone: "", email: "", address: "", addressAr: "", taxNumber: "", receiptFooter: "", receiptFooterAr: "" };

/** The business identity printed on thermal receipts, A4 invoices and quotations. */
export function CompanyPanel() {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const { data } = useCompany();
  const [f, setF] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) setF(data);
  }, [data]);

  const field = (key: keyof Form, label: string, opts: { dir?: "ltr" | "rtl"; max?: number; mono?: boolean } = {}) => (
    <div>
      <label className="label" htmlFor={`co-${key}`}>{label}</label>
      <input id={`co-${key}`} className={`field ${opts.mono ? "num font-mono" : ""}`} dir={opts.dir} maxLength={opts.max ?? 160} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} />
    </div>
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings/company", f);
      await qc.invalidateQueries({ queryKey: keys.company });
      toast(t("company.saved"), "success");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="card space-y-3 p-4 sm:p-5" aria-label={t("company.title")}>
      <SectionHeader icon="🏢" title={t("company.title")} subtitle={t("company.subtitle")} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {field("stationNameAr", t("company.nameAr"), { dir: "rtl" })}
        {field("stationName", t("company.nameEn"), { dir: "ltr" })}
        {field("addressAr", t("company.addressAr"), { dir: "rtl", max: 200 })}
        {field("address", t("company.addressEn"), { dir: "ltr", max: 200 })}
        {field("phone", t("company.phone"), { dir: "ltr", mono: true, max: 40 })}
        {field("email", t("company.email"), { dir: "ltr", max: 120 })}
        {field("taxNumber", t("company.taxNo"), { dir: "ltr", mono: true, max: 40 })}
        <div className="hidden sm:block" />
        {field("receiptFooterAr", t("company.footerAr"), { dir: "rtl" })}
        {field("receiptFooter", t("company.footerEn"), { dir: "ltr" })}
      </div>
      <p className="text-[11px] text-gray-500">{t("company.hint")}</p>
      <button type="submit" className="btn-primary" disabled={busy || !f.stationName.trim() || !f.stationNameAr.trim()}>{t("company.save")}</button>
    </form>
  );
}
